import createCachedSelector from "re-reselect";
import { ApplicationStore } from "src/store";
import { DealerLeaseOffer } from "Types/api/DealerLeaseOffer";
import { DealerLoanOffer } from "Types/api/DealerLoanOffer";
import { DealerOffer } from "Types/api/DealerOffer";
import { DealerOfferListItem } from "Types/api/DealerOfferListItem";
import { OfferFrequency } from "Types/api/OfferFrequency";
import { VehiclePaymentType } from "Types/api/VehiclePayments";

export const hasDealerOfferByVin = (state: ApplicationStore, vin: string): boolean => vin in (state?.dealerOffer ?? {});

export const isDealerOfferSourceInstantApproval = (state: ApplicationStore, vin: string): boolean => {
    return vin in (state?.dealerOffer ?? {}) && state?.dealerOffer?.[vin]?.source === "Instant Credit";
};

export const getDealerOfferByVin = (state: ApplicationStore, vin: string): DealerOffer | undefined =>
    state?.dealerOffer?.[vin];

export const getDealerOfferPriceByVin = (state: ApplicationStore, vin: string): number =>
    state?.dealerOffer?.[vin]?.price ?? 0;

export const getDealerOfferTypeByVin = (state: ApplicationStore, vin: string): VehiclePaymentType | undefined =>
    state?.dealerOffer?.[vin]?.type;

export const getDealerOfferTermByVin = (state: ApplicationStore, vin: string): number => {
    const offer = getDealerOfferByVin(state, vin);
    if (offer && "term" in offer) {
        return offer.term;
    }

    return 0;
};

export const getDealerOfferDownPaymentByVin = (state: ApplicationStore, vin: string): number =>
    (state?.dealerOffer?.[vin] as DealerLoanOffer | DealerLeaseOffer)?.down_payment ?? 0;

export const getDealerOfferPaymentByVin = (state: ApplicationStore, vin: string): number =>
    (state?.dealerOffer?.[vin] as DealerLoanOffer | DealerLeaseOffer)?.payment ?? 0;

export const getDealerOfferFrequencyByVin = (state: ApplicationStore, vin: string): OfferFrequency | undefined =>
    (state?.dealerOffer?.[vin] as DealerLoanOffer | DealerLeaseOffer)?.frequency;

export const getDealerOfferAmountFinancedByVin = (state: ApplicationStore, vin: string): number =>
    (state?.dealerOffer?.[vin] as DealerLoanOffer | DealerLeaseOffer)?.amount_financed ?? 0;

export const getDealerOfferLenderByVin = (state: ApplicationStore, vin: string): string | undefined =>
    (state?.dealerOffer?.[vin] as DealerLoanOffer | DealerLeaseOffer)?.lender;

export const getDealerOfferInterestRateByVin = (state: ApplicationStore, vin: string): number | undefined =>
    (state?.dealerOffer?.[vin] as DealerLoanOffer | DealerLeaseOffer)?.interest_rate;

export const getDealerOfferDistanceByVin = (state: ApplicationStore, vin: string) =>
    (state?.dealerOffer?.[vin] as DealerLeaseOffer)?.miles ?? 0;

export const getDealerOfferTradeInAllowanceByVin = (state: ApplicationStore, vin: string): number =>
    state?.dealerOffer?.[vin]?.tradein?.allowance ?? 0;

export const getDealerOfferTradeInCashOutByVin = (state: ApplicationStore, vin: string): number =>
    state?.dealerOffer?.[vin]?.tradein?.cash_out ?? 0;

export const getDealerOfferTradeInBalanceByVin = (state: ApplicationStore, vin: string): number =>
    state?.dealerOffer?.[vin]?.tradein?.balance ?? 0;

export const getDealerOfferTotalIncentivesByVin = (state: ApplicationStore, vin: string): number =>
    (state?.dealerOffer?.[vin]?.rebates || 0).reduce(
        (total: number, rebate: DealerOfferListItem) => (total += rebate.value),
        0
    );

export const getDealerOfferAccessoriesByVin = (state: ApplicationStore, vin: string) => {
    return state?.dealerOffer?.[vin]?.accessories ?? [];
};

export const getDealerOfferWarrantiesByVin = (state: ApplicationStore, vin: string) => {
    return state?.dealerOffer?.[vin]?.warranties ?? [];
};

export const getDealerOfferTotalAccessoriesByVin = createCachedSelector(
    (state: ApplicationStore, vin: string) => state?.dealerOffer?.[vin],
    (dealerOffer: DealerOffer) => {
        return (
            dealerOffer?.accessories?.reduce(
                (total: number, accessory: DealerOfferListItem) => total + (accessory.value ?? 0),
                0
            ) ?? 0
        );
    }
)((state, vin) => vin);

export const getDealerOfferFeesByVin = (state: ApplicationStore, vin: string) => {
    return state?.dealerOffer?.[vin]?.fees ?? [];
};

export const getDealerOfferTaxesByVin = (state: ApplicationStore, vin: string) => {
    return state?.dealerOffer?.[vin]?.taxes ?? [];
};

export const getDealerOfferRebatesByVin = (state: ApplicationStore, vin: string) => {
    return state?.dealerOffer?.[vin]?.rebates ?? [];
};

export const getDealerOfferTotalFeesByVin = (state: ApplicationStore, vin: string): number =>
    (state?.dealerOffer?.[vin]?.fees || 0).reduce((total: number, fee: DealerOfferListItem) => (total += fee.value), 0);

export const getDealerOfferTotalNonMonthlyTaxesByVin = (state: ApplicationStore, vin: string): number =>
    (state?.dealerOffer?.[vin]?.taxes || 0).reduce(
        (total: number, tax: DealerOfferListItem) => (total += tax.value),
        0
    );
